'use client';

// Durable delivery of BRC-29 claim data. By the time a payment is recorded the
// coins are already spent, and derivationPrefix/derivationSuffix are the ONLY
// data the recipient needs to internalize/claim the output. So we persist each
// record to localStorage first, then retry the POST until the server
// acknowledges it. Server-side UNIQUE(txid) makes replayed retries safe.
//
// Unlike cookie-clucker (single /api/payments endpoint), Pelf has multiple
// payment purposes, so each record carries the endpoint it must be posted to
// (e.g. "/api/crystals/purchase", "/api/ladder/enter").

const KEY = 'pelf.pendingPayments.v1';

export type PaymentRecord = {
  /** API route the record is posted to, e.g. "/api/crystals/purchase". */
  endpoint: string;
  txid: string;
  satoshis: number;
  /** Payer's identity public key (compressed hex). */
  payerKey: string;
  derivationPrefix: string;
  derivationSuffix: string;
  /**
   * Signed transaction in AtomicBEEF format (byte array). Lets the server
   * wallet SPV-verify and internalize the payment without a chain lookup.
   * Optional: older queued records (pre-upgrade) won't have it, and the
   * server degrades to optimistic acceptance when it's absent.
   */
  tx?: number[];
  /** What the payment was for, e.g. "crystals" | "ladder-entry". */
  purpose: string;
  /** Optional purpose-specific payload (pack id, ladder id, ...). */
  extra?: Record<string, unknown>;
};

function read(): PaymentRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as PaymentRecord[]) : [];
  } catch {
    return [];
  }
}

function write(list: PaymentRecord[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage full / unavailable — best effort */
  }
}

async function post(rec: PaymentRecord): Promise<boolean> {
  try {
    const res = await fetch(rec.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rec),
    });
    // 2xx = accepted; 409 = already recorded (duplicate txid) — also "done".
    return res.ok || res.status === 409;
  } catch {
    return false;
  }
}

// Persist a payment, then attempt delivery. Returns once the flush pass
// completes (the record stays queued if delivery failed).
export async function recordPayment(rec: PaymentRecord): Promise<void> {
  const list = read();
  list.push(rec);
  write(list);
  await flushPaymentQueue();
}

let flushing = false;

// Retry every queued record; drop the ones the server acknowledges.
export async function flushPaymentQueue(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    const list = read();
    if (list.length === 0) return;
    const remaining: PaymentRecord[] = [];
    for (const rec of list) {
      const ok = await post(rec);
      if (!ok) remaining.push(rec);
    }
    write(remaining);
  } finally {
    flushing = false;
  }
}

export function pendingPaymentCount(): number {
  return read().length;
}
